// What one device socket is subscribed to, and where it has got to (docs/08 "Client
// edge", "Reliability"): pure bookkeeping the client consults to build `subscribe`
// frames, including the `after` cursors a reconnect resumes from. No I/O.
import { bytesToHex } from "../proto/bytes";
import type { ClientFrame, Cursor, IdBytes, Subscription } from "../proto/frames";

interface SegmentProgress {
  conversation: string;
  cursor: Cursor;
}

export class SubscriptionSet {
  private nextId = 1;
  private readonly subs = new Map<number, Subscription>();
  /** Highest seq seen per segment (hex segment id → progress). */
  private readonly progress = new Map<string, SegmentProgress>();

  add(sub: Subscription): number {
    const id = this.nextId++;
    this.subs.set(id, sub);
    return id;
  }

  remove(id: number): Subscription | undefined {
    const sub = this.subs.get(id);
    this.subs.delete(id);
    return sub;
  }

  get size(): number {
    return this.subs.size;
  }

  /**
   * Learn from an event the API sent so a resubscribe can say `after`. A cursor names the
   * highest seq seen; the API replays what follows it.
   */
  observe(frame: ClientFrame): void {
    if (frame.type !== "event") return;
    const { conversation_id, segment_id, seq } = frame.event.header;
    const key = bytesToHex(segment_id);
    const seen = this.progress.get(key)?.cursor.seq ?? -1;
    if (seq > seen) this.progress.set(key, { conversation: bytesToHex(conversation_id), cursor: { segment_id, seq } });
  }

  /** The `subscribe` frame for `sub`, resuming after everything already seen. */
  subscribeFrame(sub: Subscription): ClientFrame {
    return {
      type: "subscribe",
      conversations: sub.conversations,
      runs: sub.runs ?? [],
      terminal: sub.terminal,
      after: sub.after ?? this.cursorsFor(sub),
      ...tasksOf(sub.task_groups ?? [], sub.tasks ?? []),
    };
  }

  /**
   * The `unsubscribe` frame for a removed `sub`. The API keeps one set of watched task groups
   * and tasks per socket, so ids another live subscription still names are left out.
   */
  unsubscribeFrame(sub: Subscription): ClientFrame {
    const kept = (ids: IdBytes[] | undefined, pick: (s: Subscription) => IdBytes[] | undefined) => {
      const still = new Set(Array.from(this.subs.values(), (s) => (pick(s) ?? []).map(bytesToHex)).flat());
      return (ids ?? []).filter((id) => !still.has(bytesToHex(id)));
    };
    return {
      type: "unsubscribe",
      conversations: sub.conversations,
      runs: sub.runs ?? [],
      terminal: sub.terminal,
      ...tasksOf(
        kept(sub.task_groups, (s) => s.task_groups),
        kept(sub.tasks, (s) => s.tasks),
      ),
    };
  }

  /** One `subscribe` per live subscription, for a fresh socket. */
  resubscribeFrames(): ClientFrame[] {
    return Array.from(this.subs.values(), (sub) => this.subscribeFrame(sub));
  }

  private cursorsFor(sub: Subscription): Cursor[] {
    const scope = sub.conversations;
    if (scope === undefined) return [];
    const wanted = scope === "all" ? undefined : new Set(scope.ids.map((id: IdBytes) => bytesToHex(id)));
    return Array.from(this.progress.values())
      .filter((p) => wanted === undefined || wanted.has(p.conversation))
      .map((p) => p.cursor);
  }
}

/** The task fields of a frame, left out when empty as the Rust encoder leaves them out. */
function tasksOf(task_groups: IdBytes[], tasks: IdBytes[]): Pick<Subscription, "task_groups" | "tasks"> {
  return { ...(task_groups.length > 0 ? { task_groups } : {}), ...(tasks.length > 0 ? { tasks } : {}) };
}
